"use client";
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { OptimizedImage } from '@/components/ui/OptimizedImage';

const smallBusinessHero = '/assets/solutions/small-business-hero.webp';
const startupHero = '/assets/solutions/startup-hero.webp';
const staffingHero = '/assets/solutions/why-staffing-agencies.webp';
const enterpriseHero = '/assets/solutions/big-business.webp';

const segments = [
  {
    label: 'Small Businesses',
    slug: 'business-type-small-business-recruiting-software',
    title: 'Streamline Hiring for Small Businesses',
    description: 'Small businesses often lack dedicated HR teams. Hirebee automates job posting, candidate screening, and interview scheduling — so you can hire top talent without the overhead of expensive recruitment tools or agencies.',
    image: smallBusinessHero,
  },
  {
    label: 'Startups',
    slug: 'business-type-recruitment-software-for-startups',
    title: 'Scale Your Startup Team Faster',
    description: 'Startups need to move fast. Hirebee helps you build your dream team with AI-powered candidate matching, collaborative hiring workflows, and automated outreach — all designed for lean teams with big ambitions.',
    image: startupHero,
  },
  {
    label: 'Staffing Agency',
    slug: 'business-type-staffing-agency-recruiting-software',
    title: 'Supercharge Your Staffing Agency',
    description: 'Manage hundreds of open roles and thousands of candidates effortlessly. Hirebee gives staffing agencies powerful bulk actions, client portals, and pipeline analytics to place candidates faster than ever.',
    image: staffingHero,
  },
  {
    label: 'Enterprise',
    slug: 'business-type-enterprise-recruitment-software',
    title: 'Enterprise-Grade Recruitment at Scale',
    description: 'Large organizations need robust compliance, advanced reporting, and seamless integrations. Hirebee delivers enterprise-level security, SSO, custom workflows, and dedicated support for global hiring operations.',
    image: enterpriseHero,
  },
];

export function WhoIsIdealSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = segments[activeIndex];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Who is Hirebee ideal for?
          </h2>
          <p className="text-lg text-muted-foreground">
            Whether you're a growing startup or a global enterprise, Hirebee adapts to your unique recruitment needs with powerful, flexible tools.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {segments.map((seg, i) => (
            <button
              key={seg.slug}
              onClick={() => setActiveIndex(i)}
              className={`px-5 py-2.5 rounded-[16px] text-sm font-medium transition-all duration-200 ${
                i === activeIndex
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground border border-border'
              }`}
            >
              {seg.label}
            </button>
          ))}
        </div>

        {/* Content Card */}
        <Link to={`/${active.slug}`} className="block group">
          <div className="bg-background rounded-2xl shadow-lg border border-border overflow-hidden transition-shadow duration-200 group-hover:shadow-xl">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image */}
              <div className="flex items-center justify-center bg-muted/20">
                <OptimizedImage
                  src={active.image}
                  alt={active.title}
                  width={600}
                  height={400}
                  className="w-full h-auto object-contain"
                />
              </div>

              {/* Text */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  {active.title}
                </h3>
                <p className="text-muted-foreground mb-8 leading-relaxed">
                  {active.description}
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center text-sm md:text-base font-bold uppercase tracking-wide text-primary group-hover:underline">
                    Read More <ArrowRight className="ml-1 h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Link>

        {/* Bottom CTA */}
        <div className="text-center mt-10">
          <Button asChild size="lg" className="h-12 px-8 text-base font-bold uppercase tracking-wide bg-gradient-primary hover:opacity-90 text-white shadow-md">
            <Link to="/solutions">
              Explore All Solutions <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
